import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import { useGrowStore } from "../../hooks/useGrowStore";
import { currentUser, badges } from "../../mocks/dashboard";
import { roadmapTracks } from "../../mocks/roadmap";
import { exploreClasses } from "../../mocks/explore";

const STORE_BADGE_MAP: Record<string, { icon: string; name: string }> = {
  "badge-first": { icon: "🎓", name: "첫 졸업" },
  "badge-3class": { icon: "📚", name: "열공중" },
  "badge-expert": { icon: "👑", name: "전문가 인증" },
  "badge-highscore": { icon: "⭐", name: "우등생" },
};

function CopyToast({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-[#191F28] text-white px-6 py-3.5 rounded-2xl flex items-center gap-3"
      style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}
    >
      <i className="ri-check-line text-[#00D873] text-lg" />
      <span className="text-sm font-bold">링크가 복사됐어요!</span>
      <button onClick={onClose} className="ml-2 text-white/40 hover:text-white cursor-pointer">
        <i className="ri-close-line" />
      </button>
    </div>
  );
}

function RingProgress({ pct, color, size = 56 }: { pct: number; color: string; size?: number }) {
  const r = (size / 2) * 0.76;
  const circumference = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#F2F4F6" strokeWidth="5" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="5"
        strokeDasharray={`${(pct / 100) * circumference} ${circumference}`} strokeLinecap="round"
        style={{ transition: "stroke-dasharray 0.8s ease" }} />
    </svg>
  );
}

function ScorePill({ score }: { score: number }) {
  const color = score >= 90 ? "#00D873" : score >= 80 ? "#FF8C00" : score >= 70 ? "#E91E63" : "#8B95A1";
  return (
    <span className="text-xs font-extrabold px-2.5 py-1 rounded-full" style={{ backgroundColor: `${color}18`, color }}>
      {score}점
    </span>
  );
}

export default function PortfolioPage() {
  const { store, isClassCompleted } = useGrowStore();
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      setCopied(false);
    }
  };

  const earnedBadges = useMemo(() => [
    ...badges.filter((b) => b.earned),
    ...Object.entries(STORE_BADGE_MAP)
      .filter(([id]) => store.earnedBadgeIds.includes(id))
      .map(([, b], i) => ({ id: 100 + i, ...b, earned: true })),
  ], [store.earnedBadgeIds]);

  const lockedBadges = useMemo(() => [
    ...badges.filter((b) => !b.earned),
    ...Object.entries(STORE_BADGE_MAP)
      .filter(([id]) => !store.earnedBadgeIds.includes(id))
      .map(([, b], i) => ({ id: 200 + i, ...b, earned: false })),
  ], [store.earnedBadgeIds]);

  const trackProgress = useMemo(() =>
    roadmapTracks.map((t) => {
      const done = t.steps.filter((s) => isClassCompleted(s.classId)).length;
      return { ...t, done, pct: Math.round((done / t.steps.length) * 100) };
    }),
  [isClassCompleted]);

  const avgScore = useMemo(() => {
    if (!store.completedClasses.length) return 0;
    return Math.round(store.completedClasses.reduce((s, c) => s + c.avgScore, 0) / store.completedClasses.length);
  }, [store.completedClasses]);

  const stats = [
    { label: "Grow Points", value: `${store.totalPoints.toLocaleString()}P`, icon: "ri-coin-line", color: "#FF8C00" },
    { label: "완료 클래스", value: `${store.completedClasses.length}개`, icon: "ri-graduation-cap-line", color: "#00D873" },
    { label: "획득 배지", value: `${earnedBadges.length}개`, icon: "ri-medal-line", color: "#E91E63" },
    { label: "평균 점수", value: avgScore ? `${avgScore}점` : "-", icon: "ri-bar-chart-2-line", color: "#7C3AED" },
  ];

  return (
    <div className="min-h-screen bg-[#F2F4F6] font-sans">
      <Navbar />

      <div className="max-w-3xl mx-auto px-4 pt-24 pb-16">

        {/* ── Hero Card ── */}
        <div className="bg-[#191F28] rounded-3xl p-8 mb-6 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-56 h-56 bg-[#00D873]/10 rounded-full translate-x-20 -translate-y-20 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/3 rounded-full -translate-x-10 translate-y-10 pointer-events-none" />

          <div className="relative z-10">
            {/* Share button */}
            <div className="flex items-center justify-between mb-6">
              <span className="text-xs font-bold bg-[#00D873]/20 text-[#00D873] px-3 py-1.5 rounded-full">
                🌱 GrowSeo 포트폴리오
              </span>
              <button
                onClick={handleCopy}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-colors text-white text-xs font-bold px-4 py-2 rounded-full cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-line" />
                링크 공유하기
              </button>
            </div>

            {/* Profile */}
            <div className="flex items-center gap-5">
              <img
                src={currentUser.avatar}
                alt={currentUser.name}
                className="w-20 h-20 rounded-2xl object-cover object-top border-2 border-[#00D873]/30 flex-shrink-0"
              />
              <div>
                <h1 className="text-2xl font-black text-white mb-1">{currentUser.name}</h1>
                <p className="text-white/60 text-sm">{currentUser.levelIcon} {currentUser.level}</p>
                <div className="flex items-center gap-1.5 mt-2">
                  <i className="ri-fire-line text-[#FF8C00] text-sm" />
                  <span className="text-[#FF8C00] text-xs font-bold">{currentUser.streak}일 연속 학습</span>
                </div>
              </div>
              <div className="ml-auto text-right hidden sm:block">
                <div className="text-3xl font-black text-[#00D873]">{store.totalPoints.toLocaleString()}</div>
                <div className="text-white/40 text-xs mt-0.5">Grow Points</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Stats Row ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {stats.map((s) => (
            <div key={s.label} className="bg-white rounded-2xl p-4">
              <div className="w-8 h-8 flex items-center justify-center mb-2" style={{ color: s.color }}>
                <i className={`${s.icon} text-xl`} />
              </div>
              <div className="text-xl font-black text-[#191F28]">{s.value}</div>
              <div className="text-xs text-[#8B95A1] mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── Completed Classes ── */}
        <div className="bg-white rounded-3xl p-7 mb-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-black text-[#191F28]">완료한 클래스</h2>
            <Link to="/explore" className="text-sm font-bold text-[#00D873] cursor-pointer hover:opacity-80 whitespace-nowrap">
              더 배우기 →
            </Link>
          </div>

          {store.completedClasses.length === 0 ? (
            <div className="py-10 flex flex-col items-center gap-3 bg-[#F9F9F9] rounded-2xl">
              <i className="ri-book-open-line text-4xl text-[#C8CDD4]" />
              <p className="text-sm text-[#8B95A1]">아직 완료한 클래스가 없어요</p>
              <button onClick={() => navigate("/explore")} className="bg-[#00D873] text-white text-xs font-bold px-5 py-2.5 rounded-full cursor-pointer whitespace-nowrap">
                첫 클래스 시작하기 →
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {store.completedClasses.map((cls) => {
                const exploreInfo = exploreClasses.find((e) => e.id === cls.classId);
                return (
                  <div key={cls.classId} className="flex items-center gap-4 p-4 bg-[#F9F9F9] rounded-2xl hover:bg-[#F2F4F6] transition-colors">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: exploreInfo?.iconBg ?? "#F2F4F6" }}
                    >
                      <i className={`${exploreInfo?.icon ?? "ri-graduation-cap-line"} text-lg`} style={{ color: exploreInfo?.iconColor ?? "#8B95A1" }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-extrabold text-[#191F28] truncate">{cls.classTitle}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-[11px] text-[#8B95A1]">{cls.completedAt}</span>
                        {cls.expertVerified && (
                          <span className="text-[10px] bg-[#FFF9E6] text-[#FF8C00] font-bold px-2 py-0.5 rounded-full">👑 전문가</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <ScorePill score={cls.avgScore} />
                      <span className="text-xs font-extrabold text-[#00D873]">+{cls.pointsEarned}P</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {store.completedClasses.length > 0 && (
            <div className="mt-5 grid grid-cols-3 gap-3 pt-5 border-t border-[#F2F4F6]">
              {[
                { label: "창의성 평균", value: Math.round(store.completedClasses.reduce((s, c) => s + c.scores.creativity, 0) / store.completedClasses.length), color: "#FF8C00" },
                { label: "구체성 평균", value: Math.round(store.completedClasses.reduce((s, c) => s + c.scores.specificity, 0) / store.completedClasses.length), color: "#E91E63" },
                { label: "완성도 평균", value: Math.round(store.completedClasses.reduce((s, c) => s + c.scores.completeness, 0) / store.completedClasses.length), color: "#00D873" },
              ].map((m) => (
                <div key={m.label} className="bg-[#F9F9F9] rounded-xl p-3 text-center">
                  <div className="text-lg font-black" style={{ color: m.color }}>{m.value}점</div>
                  <div className="text-[10px] text-[#8B95A1] mt-0.5">{m.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Badges ── */}
        <div className="bg-white rounded-3xl p-7 mb-6">
          <h2 className="text-xl font-black text-[#191F28] mb-5">획득한 배지</h2>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-3 mb-4">
            {earnedBadges.map((b) => (
              <div key={b.id} className="flex flex-col items-center gap-1.5 bg-[#F9F9F9] rounded-2xl p-3">
                <span className="text-2xl">{b.icon}</span>
                <span className="text-[10px] font-semibold text-[#191F28] text-center leading-tight">{b.name}</span>
              </div>
            ))}
            {lockedBadges.slice(0, 4).map((b) => (
              <div key={b.id} className="flex flex-col items-center gap-1.5 bg-[#F9F9F9] rounded-2xl p-3 opacity-25">
                <span className="text-2xl">{b.icon}</span>
                <span className="text-[10px] font-semibold text-[#8B95A1] text-center leading-tight">{b.name}</span>
              </div>
            ))}
          </div>
          {earnedBadges.length === 0 && (
            <p className="text-sm text-[#8B95A1] text-center py-4">클래스를 완료하면 배지가 잠금 해제돼요!</p>
          )}
        </div>

        {/* ── Roadmap Track Progress ── */}
        <div className="bg-white rounded-3xl p-7 mb-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-black text-[#191F28]">학습 트랙 진행률</h2>
            <Link to="/roadmap" className="text-sm font-bold text-[#00D873] cursor-pointer hover:opacity-80 whitespace-nowrap">
              로드맵 →
            </Link>
          </div>
          <div className="space-y-5">
            {trackProgress.map((t) => (
              <div key={t.id} className="flex items-center gap-4">
                <div className="relative w-14 h-14 flex-shrink-0">
                  <RingProgress pct={t.pct} color={t.color} size={56} />
                  <div className="absolute inset-0 flex items-center justify-center text-xl">{t.emoji}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-extrabold text-[#191F28] truncate pr-2">{t.title}</span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {t.pct === 100 && (
                        <span className="text-[10px] font-bold text-[#00D873] bg-[#00D873]/10 px-2 py-0.5 rounded-full">완료</span>
                      )}
                      <span className="text-sm font-black" style={{ color: t.color }}>{t.pct}%</span>
                    </div>
                  </div>
                  <div className="h-2 bg-[#F2F4F6] rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${t.pct}%`, backgroundColor: t.color }} />
                  </div>
                  <div className="text-[10px] text-[#8B95A1] mt-1.5">{t.done}/{t.steps.length} 클래스 완료</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── CTA for Share ── */}
        <div className="bg-[#191F28] rounded-3xl p-7 text-center">
          <div className="text-3xl mb-3">🌱</div>
          <h3 className="text-xl font-black text-white mb-2">AI 학습 여정을 공유해보세요</h3>
          <p className="text-white/50 text-sm mb-5">이 포트폴리오 링크를 친구에게 보내고 함께 성장해요</p>
          <div className="flex gap-3 justify-center flex-wrap">
            <button
              onClick={handleCopy}
              className="bg-[#00D873] text-white font-bold text-sm px-7 py-3.5 rounded-full cursor-pointer hover:opacity-90 transition-all whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-link text-base" />
              링크 복사하기
            </button>
            <Link
              to="/explore"
              className="bg-white/10 text-white font-bold text-sm px-7 py-3.5 rounded-full cursor-pointer hover:bg-white/20 transition-all whitespace-nowrap"
            >
              더 배우러 가기 →
            </Link>
          </div>
        </div>

        {/* Footer attribution */}
        <p className="text-center text-xs text-[#8B95A1] mt-6">
          이 포트폴리오는 <span className="font-bold text-[#00D873]">GrowSeo</span>에서 생성됐어요
        </p>
      </div>

      {copied && <CopyToast onClose={() => setCopied(false)} />}
    </div>
  );
}
